import { Link } from 'react-router-dom';
import { Card } from '@/components/Card/Card';
import { Avatar } from '@/components/Avatar/Avatar';
import { t } from '@/lib/i18n';
import { formatPersonName, formatElapsedTime } from '@/lib/format';
import type { Member } from '@/features/members/schemas';
import type { JobListItem } from '@/features/jobs/schemas';
import {
  type MemberWorkingStatus,
  memberStatus,
  currentJobTitleFor,
  currentJobStartedAtFor,
} from '@/features/members/utils';
import styles from './TeamRosterMini.module.css';

export type { MemberWorkingStatus } from '@/features/members/utils';
export { memberStatus, currentJobTitleFor, currentJobStartedAtFor } from '@/features/members/utils';

const STATUS_DOT_CLASS: Record<MemberWorkingStatus, string> = {
  working: styles.statusWorking!,
  idle: styles.statusIdle!,
  blocked: styles.statusBlocked!,
};

export interface TeamRosterMiniProps {
  members: Member[];
  jobs: JobListItem[];
}

export function TeamRosterMini({ members, jobs }: TeamRosterMiniProps) {
  return (
    <Card padding="md">
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('Team')}</h2>
          <div className={styles.actions}>
            <Link to="/team/add" className={styles.actionLink}>
              {t('+ Add')}
            </Link>
            <Link to="/team" className={styles.actionLink}>
              {t('All members →')}
            </Link>
          </div>
        </div>
        <ul className={styles.list}>
          {members.map((m) => {
            const name = formatPersonName(m);
            const status = memberStatus(m, jobs);
            const workingOn = currentJobTitleFor(m, jobs);
            const startedAt = currentJobStartedAtFor(m, jobs);
            const subtitleParts = [
              ...m.positions.map((p) => p.name),
              ...m.units.map((u) => u.name),
            ];
            return (
              <li key={m.id} className={styles.row}>
                <div className={styles.avatarWrap} data-testid="avatar-wrap">
                  <Link
                    to={`/team/m/${encodeURIComponent(m.id)}`}
                    tabIndex={-1}
                    aria-hidden="true"
                    className={styles.avatarLink}
                  >
                    <Avatar name={name} size="md" kind={m.kind} />
                  </Link>
                  <span
                    className={[styles.statusDot, STATUS_DOT_CLASS[status]].join(' ')}
                    data-testid="status-dot"
                    aria-hidden="true"
                  />
                </div>
                <div className={styles.info}>
                  <Link to={`/team/m/${encodeURIComponent(m.id)}`} className={styles.nameLink}>
                    {name}
                  </Link>
                  {subtitleParts.length > 0 && (
                    <span className={styles.role}>{subtitleParts.join(' · ')}</span>
                  )}
                </div>
                <div className={styles.right}>
                  {status === 'working' && workingOn && (
                    <>
                      <span className={styles.jobName}>{workingOn}</span>
                      {startedAt && (
                        <span className={styles.elapsed}>{formatElapsedTime(startedAt)}</span>
                      )}
                    </>
                  )}
                  {status === 'idle' && <span className={styles.idleLabel}>{t('Idle')}</span>}
                  {status === 'blocked' && (
                    <span className={styles.blockedLabel}>{t('Blocked')}</span>
                  )}
                </div>
                <Link
                  to={`/team/m/${encodeURIComponent(m.id)}/chat`}
                  className={styles.chatButton}
                  aria-label={t('Chat with {name}', { name })}
                >
                  {t('Chat')}
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </Card>
  );
}
